import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

import { AppText, Button, Spinner } from '@zippeditoolsjs/ui-web';

const FACTOR_NAME_KEYS = {
  totp: 'heimdal_app.TOTP.factor_totp_name',
  sms: 'heimdal_app.TOTP.factor_sms_name',
};

const HANDOFF = { COUNTING: 'counting', ENTERING: 'entering', FAILED: 'failed' };

export default function TotpSuccess({
  enrolledFactors = [],
  autoEnter = false,
  seconds = 3,
  onEnter,
}) {
  const { t } = useTranslation();

  const [progress, setProgress] = useState(0);
  const [handoff, setHandoff] = useState(HANDOFF.COUNTING);
  const handoffRef = useRef(HANDOFF.COUNTING);
  const duration = seconds * 1000;

  const goToHandoff = phase => {
    handoffRef.current = phase;
    setHandoff(phase);
  };

  // Via ref: the countdown effect never re-runs on re-render, so a captured onEnter
  // would freeze on the handler from the first render while the button calls the latest.
  const onEnterRef = useRef(onEnter);
  useEffect(() => {
    onEnterRef.current = onEnter;
  }, [onEnter]);

  const enterNow = () => {
    if (handoffRef.current === HANDOFF.ENTERING) return;
    goToHandoff(HANDOFF.ENTERING);
    setProgress(1);
    Promise.resolve(onEnterRef.current?.()).catch(() => {
      goToHandoff(HANDOFF.FAILED);
      setProgress(0);
    });
  };

  useEffect(() => {
    if (!autoEnter) return undefined;
    // A hidden tab stops the frames but not the clock they carry, so elapsed time is
    // summed per visible stretch: reading one start mark would charge the countdown
    // for the whole time away and hand off the instant the user came back.
    let seenSoFar = 0;
    let stretchStart = null;
    let frame;

    const tick = now => {
      if (handoffRef.current !== HANDOFF.COUNTING) return;
      const p = Math.min(1, (seenSoFar + (now - stretchStart)) / duration);
      setProgress(p);
      if (p < 1) frame = requestAnimationFrame(tick);
      else enterNow();
    };

    // A null stretchStart is what "paused" means here, and it keeps both halves
    // idempotent: a repeated event cannot double-count or leave a frame running.
    const resume = () => {
      if (stretchStart !== null) return;
      stretchStart = performance.now();
      frame = requestAnimationFrame(tick);
    };
    const pause = () => {
      if (stretchStart === null) return;
      cancelAnimationFrame(frame);
      seenSoFar += performance.now() - stretchStart;
      stretchStart = null;
    };

    const onVisibilityChange = () =>
      document.visibilityState === 'hidden' ? pause() : resume();

    if (document.visibilityState !== 'hidden') resume();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
    // enterNow is stable via the ref guard; duration is the only real input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoEnter, duration]);

  // Round up and clamp to 1 so the label never shows "0 s" before handing off.
  const remaining = Math.max(1, Math.ceil((duration * (1 - progress)) / 1000));

  // Driven off the key map, not the input: unknown factors drop, duplicates
  // collapse, and the result can never exceed the pair key's two slots.
  const names = Object.keys(FACTOR_NAME_KEYS)
    .filter(f => (enrolledFactors || []).includes(f))
    .map(f => t(FACTOR_NAME_KEYS[f]));
  // The join lives in i18n: `${a} ${and} ${b}` ships western spaces into
  // Japanese, where the conjunction takes none.
  const factorList =
    names.length > 1
      ? t('heimdal_app.TOTP.success_factors_pair', { first: names[0], second: names[1] })
      : names[0] || '';

  return (
    <div className="flex flex-col items-center text-center">
      <div className="mb-5 flex h-[72px] w-[72px] items-center justify-center rounded-full border border-solid border-success-tint bg-success-tint">
        <IconComponent iconName="checkmark" style={{ fontSize: 34, color: 'var(--zippedi-success-strong)' }} />
      </div>
      <AppText variant="titleXLarge" className="mb-2">
        {t('heimdal_app.TOTP.success_title')}
      </AppText>
      <AppText variant="bodyMedium" className="mb-6 max-w-[280px] text-secondary">
        {t('heimdal_app.TOTP.success_subtitle')}
      </AppText>
      {factorList ? (
        // surface-raised / line-soft have no dark value, so without the dark: pair
        // the badge stays near-white while text-primary turns white.
        <div className="mb-6 inline-flex items-center gap-1.5 rounded-full border border-solid border-muted bg-surface-hover px-3 py-1 dark:border-border dark:bg-surface">
          <IconComponent iconName="shield-checkmark" style={{ fontSize: 14, color: 'var(--zippedi-accent)' }} />
          <span className="font-sans text-xs font-semibold text-primary">
            {t('heimdal_app.TOTP.success_factors_label', { factors: factorList })}
          </span>
        </div>
      ) : null}
      {autoEnter ? (
        <div className="flex w-full flex-col items-center">
          {/* Fixed height and an absolute spinner so swapping label for spinner
              never shifts the text off the row's centre. */}
          <div className="relative flex h-7 w-full items-center justify-center">
            {handoff === HANDOFF.ENTERING && (
              <span className="relative inline-flex items-center font-sans text-[13px] font-semibold text-secondary">
                <Spinner tone="success" className="absolute right-full mr-2" />
                {t('heimdal_app.TOTP.entering_short')}
              </span>
            )}
            {handoff === HANDOFF.COUNTING && (
              <span className="font-sans text-[13px] text-secondary tabular-nums">
                <Trans
                  i18nKey="heimdal_app.TOTP.entering_countdown"
                  values={{ seconds: remaining }}
                  components={{ b: <strong className="font-bold text-primary" /> }}
                />
              </span>
            )}
          </div>
          <Button
            variant="primary"
            disabled={handoff === HANDOFF.ENTERING}
            onClick={enterNow}
            // `!h-11` beats the variant's h-[50px]; once entering, the shared
            // disabled skin (opacity-60) fades label and progress fill together.
            className="relative mt-1 !h-11 w-4/5 overflow-hidden">
            {/* Fill draws behind the label, which rides on `relative` to stay
                above it — that layering is why `startIcon` is not used. */}
            <span
              aria-hidden="true"
              className="absolute inset-y-0 left-0 bg-success"
              style={{ width: `${progress * 100}%` }}
            />
            {/* Absolute arrow so it does not shift the label off the button's centre. */}
            <span className="relative inline-flex items-center font-sans">
              {t('heimdal_app.TOTP.enter_now')}
              <IconComponent
                iconName="arrow-forward"
                style={{ fontSize: 16 }}
                className="absolute left-full ml-2"
              />
            </span>
          </Button>
        </div>
      ) : null}
    </div>
  );
}

TotpSuccess.propTypes = {
  enrolledFactors: PropTypes.arrayOf(PropTypes.oneOf(['totp', 'sms'])),
  autoEnter: PropTypes.bool,
  seconds: PropTypes.number,
  onEnter: PropTypes.func,
};
